import React, { useState, useEffect } from "react";
import { reportsAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { BarChart3, Package, Container, RefreshCw, Download } from "lucide-react";
import { fmtDateTime } from "../utils/dateUtils";

const STATUS_COLORS = {
  optimal: "bg-[#EDF2EE] text-[#637C64]",
  minimum: "bg-[#FDF4EB] text-[#D98032]",
  critical: "bg-[#FBEAE7] text-[#C2624E]",
};

const tabs = [
  { id: "stock", label: "Stock Summary", icon: Package },
  { id: "vessels", label: "Vessel Report", icon: Container },
  { id: "gate", label: "Gate Entries", icon: BarChart3 },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState("stock");
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen: setSelectedKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);
  const [stockData, setStockData] = useState([]);
  const [vesselData, setVesselData] = useState([]);
  const [gateData, setGateData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchReport(); }, [activeTab, selectedKitchen]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = selectedKitchen ? { kitchen_id: selectedKitchen } : {};
      if (activeTab === "stock") {
        const r = await reportsAPI.getStockSummary(params);
        setStockData(r.data || []);
      } else if (activeTab === "vessels") {
        const r = await reportsAPI.getVesselReport(params);
        setVesselData(r.data || []);
      } else if (activeTab === "gate") {
        const r = await reportsAPI.getSecurityEntries(params);
        setGateData(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(","));
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reports">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Reports</h1>
        <div className="flex items-center gap-2">
          <button onClick={fetchReport} className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex border-b border-[#E5E1DB]">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* Stock Summary */}
          {activeTab === "stock" && (
            <>
              <div className="flex justify-end">
                <button onClick={() => exportCSV(stockData, "stock_summary")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export CSV
                </button>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["#", "Item", "Category", "Total Inward", "Total Issued", "Current Stock", "Min Stock", "Status"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {stockData.map((item, i) => (
                        <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-[#8A7E73]">{item.item_id}</td>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{item.name}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{item.category}</td>
                          <td className="px-4 py-3 text-[#637C64]">{item.total_inward} {item.unit}</td>
                          <td className="px-4 py-3 text-[#D98032]">{item.total_issued} {item.unit}</td>
                          <td className="px-4 py-3 font-bold">{item.current_stock} {item.unit}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{item.min_stock} {item.unit}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[item.status]}`}>
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {stockData.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8A7E73]">No stock data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Vessel Report */}
          {activeTab === "vessels" && (
            <>
              <div className="flex justify-end">
                <button onClick={() => exportCSV(vesselData, "vessel_report")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export CSV
                </button>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Route</th>
                        {["5L", "10L", "20L", "30L", "40L", "Egg", "Banana"].map(s => (
                          <React.Fragment key={s}>
                            <th className="text-center px-2 py-3 text-xs font-bold uppercase text-[#5C5147]">{s} Out</th>
                            <th className="text-center px-2 py-3 text-xs font-bold uppercase text-[#637C64]">{s} Ret</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {vesselData.map((r, i) => (
                        <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-bold text-[#E8956D]">Route {r.route}</td>
                          <td className="px-2 py-3 text-center">{r.total_5L_out}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_5L_ret}</td>
                          <td className="px-2 py-3 text-center">{r.total_10L_out}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_10L_ret}</td>
                          <td className="px-2 py-3 text-center">{r.total_20L_out}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_20L_ret}</td>
                          <td className="px-2 py-3 text-center">{r.total_30L_out}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_30L_ret}</td>
                          <td className="px-2 py-3 text-center">{r.total_40L_out}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_40L_ret}</td>
                          <td className="px-2 py-3 text-center">{r.total_egg_out || 0}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_egg_ret || 0}</td>
                          <td className="px-2 py-3 text-center">{r.total_banana_out || 0}</td><td className="px-2 py-3 text-center text-[#637C64]">{r.total_banana_ret || 0}</td>
                        </tr>
                      ))}
                      {vesselData.length === 0 && <tr><td colSpan={15} className="text-center py-10 text-[#8A7E73]">No vessel data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Gate Entries */}
          {activeTab === "gate" && (
            <>
              <div className="flex justify-end">
                <button onClick={() => exportCSV(gateData, "gate_entries")} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export CSV
                </button>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Date/Time", "Category", "Direction", "Vehicle/Route", "Notes"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {gateData.map(e => (
                        <tr key={e.id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-xs text-[#8A7E73]">{fmtDateTime(e.entry_time)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${e.entry_category === "food_distribution" ? "bg-blue-100 text-blue-700" : e.entry_category === "stores" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                              {e.entry_category?.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${e.direction === "outward" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#EDF2EE] text-[#637C64]"}`}>{e.direction}</span>
                          </td>
                          <td className="px-4 py-3 text-[#3A322C]">{e.vehicle_number || e.route_number || "—"}</td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{e.notes || "—"}</td>
                        </tr>
                      ))}
                      {gateData.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8A7E73]">No entries</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
